import { Box, Divider } from "@mui/material"
import { AddOutlined, VisibilityOffOutlined, VisibilityOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import React, { Fragment, useState } from "react"

import CommentForm from "@/components/Comment/CommentForm"
import CommentListItem from "@/components/Comment/CommentListItem"
import { Button } from "@/components/common/Button"
import type { Comment, CommentParentTableType } from "@/graphql/codegen/graphql"
import { useCommentsActions } from "@/screens/Companies/hooks"

interface CommentListProps {
  comments: Comment[]
  commentType: CommentParentTableType
  referenceId: string
  isReadOnly?: boolean
  showAddComment: boolean
  setShowAddComment: (show: boolean) => void
  onSuccess: () => void
}

const CommentList: FC<CommentListProps> = ({
  comments,
  commentType,
  referenceId,
  isReadOnly = false,
  setShowAddComment,
  showAddComment,
  onSuccess,
}) => {
  const [isArchivedVisible, setIsArchivedVisible] = useState<boolean>(false)
  const [updateCommentId, setUpdateCommentId] = useState<string>("")

  const { updateComment, createComment } = useCommentsActions()
  const handleToggleFilesVisibility = () => setIsArchivedVisible((prev) => !prev)

  const filteredComments = isArchivedVisible ? comments : comments.filter((comment) => !comment.isArchived)

  const handleCreateComment = async (value: string) => {
    await createComment({
      input: {
        referenceId,
        value,
        type: commentType,
      },
    })

    setShowAddComment(false)
    onSuccess()
  }

  const handleEditComment = async (value: string, commentId?: string) => {
    if (!commentId) return
    await updateComment({
      input: {
        commentId,
        value,
      },
    })
    setUpdateCommentId("")
    onSuccess()
  }

  return (
    <>
      <Box className='flex items-start gap-4 px-0'>
        {!isReadOnly && (
          <Button
            variant='primary'
            appearance='text'
            className='pl-0 text-sm'
            startIcon={<AddOutlined />}
            onClick={() => setShowAddComment(true)}
            data-testid='comments-add-button'
          >
            New Comment
          </Button>
        )}
        <Button
          variant='primary'
          appearance='text'
          className='size-fit p-2 text-sm'
          onClick={() => handleToggleFilesVisibility()}
          startIcon={isArchivedVisible ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
          data-testid='comments-toggle-archived-button'
        >
          {isArchivedVisible ? "Hide" : "Show"} Archived
        </Button>
      </Box>
      <Box className='w-full items-center justify-between'>
        {showAddComment && (
          <>
            <Divider />
            <CommentForm
              value=''
              commentId='new'
              onSubmitComment={handleCreateComment}
              onCancelComment={() => setShowAddComment(false)}
            />
          </>
        )}
        {filteredComments.map((comment) => {
          const { value, commentId } = comment
          if (!value) return null
          return (
            <Fragment key={commentId}>
              <Divider />
              {updateCommentId === commentId ? (
                <CommentForm
                  value={value}
                  commentId={commentId}
                  onSubmitComment={handleEditComment}
                  onCancelComment={() => setUpdateCommentId("")}
                />
              ) : (
                <CommentListItem
                  comment={comment}
                  onSuccess={onSuccess}
                  onEditComment={setUpdateCommentId}
                  isReadOnly={isReadOnly}
                />
              )}
            </Fragment>
          )
        })}
      </Box>
    </>
  )
}

export default CommentList
